import { Component, computed, input, signal } from '@angular/core'
import { AgGridAngular, ICellRendererAngularComp } from 'ag-grid-angular'
import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { ChangeSet } from './entity-management-changes.service'
import { formatDistanceToNow } from 'date-fns'
import { MatIcon } from '@angular/material/icon'
import { RouterLink } from '@angular/router'


class CellRenderer<T> implements ICellRendererAngularComp {
  protected changeSet = signal<ChangeSet | undefined>(undefined)
  protected value = signal<T | undefined>(undefined)

  agInit(params: ICellRendererParams<ChangeSet, T>) {
    this.refresh(params)
  }

  refresh(params: ICellRendererParams<ChangeSet, T>): boolean {
    this.changeSet.set(params.data)
    this.value.set(params.value ?? undefined)
    return true
  }
}

@Component({
  imports: [MatIcon],
  template: `
   <mat-icon [class]="iconName()" [fontIcon]="iconName()" />
   @if (messageCount() > 1) {
     <span class='message-count ms-1'>{{ messageCount() }}</span>
   }
  `,
  styles: `
    mat-icon {
      position: relative;
      top: 6px;
      &.error {
        color: var(--error-red);
      }
      &.check {
        color: var(--bs-success)
      }
    }
  `,
})
class Status extends CellRenderer<number> {
  protected messageCount = computed((): number => this.value() ?? 0)
  protected iconName = computed((): string => this.messageCount() > 0 ? 'error' : 'check')
}

@Component({
  imports: [RouterLink],
  template: `
    @if (changeSet()) {
      <a [routerLink]="changeSet()!.url">{{ value() }}</a>
    }
  `,
})
class ChangeLocation extends CellRenderer<string> {}

@Component({
  selector: 'app-change-list',
  imports: [AgGridAngular],
  template: `
    <ag-grid-angular
        class='app-table'
        [defaultColDef]="defaultColDef"
        [columnDefs]="colDefs"
        [rowData]="rows()"
    />
  `,
  styles: `
    :host {
      .app-table {
        height: 100%;
      }
    }
  `,
})
export class ChangeListComponent {
  readonly rows = input.required<ChangeSet[]>()

  protected readonly colDefs: ColDef[] = [
    {
      field: 'status',
      headerName: '',
      width: 70,
      valueGetter: ({ data }: ValueGetterParams<ChangeSet>) => data ? data.validationMessages.length : 0,
      cellRenderer: Status,
    },
    {
      field: 'timestamp',
      headerName: 'Last Modified',
      width: 250,
      valueFormatter: ({ value }) => formatDistanceToNow(value, { addSuffix: true }),
    },
    {
      field: 'description',
      headerName: 'Location',
      width: 200,
      valueGetter: ({ data }: ValueGetterParams<ChangeSet>) => {
        if (data) {
          const changeCount = data.changes.length
          if (changeCount > 1) {
            return `${data.description} (${data.changes.length})`
          } else {
            return data.description
          }
        } else {
          return ''
        }
      },
      cellRenderer: ChangeLocation,
    },
  ]

  protected readonly defaultColDef: ColDef = {
    sortable: false,
    editable: false,
  }
}
